import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Link, Text, tokens } from '@hotjar/hotsauce';
import { useGetTopPages, TOP_PAGES_LIMIT } from '../../hooks/useGetTopPages';
import { TranslatedSessionCountsText } from '../TranslatedSessionCountsText';
import { Container, Content, Image, ErrorTitle as Title, Subtitle } from './common';
import WaitLongerImage from '../../icons/wait-longer-error.svg';

interface WaitLongerErrorProps {
  siteId: number;
  showSuggestions?: false;
  onNavigateToHeatmap?: never;
}

interface WaitLongerErrorWithSuggestionsProps {
  siteId: number;
  showSuggestions: true;
  onNavigateToHeatmap: (url: string) => void;
}

const Suggestions = styled.div`
  margin-top: ${tokens.spacer.x2pt5};
`;

const SuggestionsTitle = styled(Text)`
  margin-bottom: ${tokens.spacer.x1pt5};
`;

const Row = styled.div`
  margin-bottom: ${tokens.spacer.x1};
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x2};
`;

const SuggestionLink = styled(Link)`
  flex-grow: 1;
  ${tokens.text('caption')}
  text-align: initial;
`;

export const WaitLongerError = ({
  showSuggestions,
  siteId,
  onNavigateToHeatmap,
}: WaitLongerErrorProps | WaitLongerErrorWithSuggestionsProps): JSX.Element => {
  const { t } = useTranslation();

  const topPages = useGetTopPages(siteId, TOP_PAGES_LIMIT.THREE);

  return (
    <Container>
      <Content>
        <Image alt="Title" src={WaitLongerImage} />
        <Title as="p" textType="subtitle1">
          {t('heatmaps:error-states.wait-longer.title')}
        </Title>
        <Subtitle as="p" textType="caption" textColor="text_light">
          {t('heatmaps:error-states.wait-longer.subtitle')}
        </Subtitle>
        {showSuggestions === true && topPages && (
          <Suggestions>
            <SuggestionsTitle as="p" textType="caption" textColor="text_light">
              {t('heatmaps:error-states.wait-longer.suggestions')}
            </SuggestionsTitle>
            {topPages.map((page) => {
              return (
                <Row key={page.pageUrl}>
                  <SuggestionLink
                    as="button"
                    kind="standalone"
                    onClick={() => {
                      if (onNavigateToHeatmap !== undefined) {
                        onNavigateToHeatmap(page.pageUrl);
                      }
                    }}
                  >
                    {page.pageUrl}
                  </SuggestionLink>

                  <Text textType="caption" textColor="text_disabled">
                    <TranslatedSessionCountsText sessionCount={page.sessionCount} />
                  </Text>
                </Row>
              );
            })}
          </Suggestions>
        )}
      </Content>
    </Container>
  );
};
